import React, { useEffect } from "react";
import VisitConceptTreeBranch from "../Molecule/VisitConceptTreeBranch";
import VisitConceptTreeLeaf from "../Atom/VisitConceptTreeLeaf";
import { Tree, TreeOpenChangeData } from "@fluentui/react-components";
import { CalendarLtr20Regular, Folder20Regular, ClipboardTaskListRtl20Regular, ClipboardCheckmark20Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useTranslation } from 'react-i18next';
import { ExtraFormType } from '../../../models/Enums/ExtraFormType';

// owner functions
import * as ComponentFunctions from "./VisitConceptTree.Functions";
import { HCConcept } from "../../../models/Entities/Protocols/HCVisit";

const VisitConceptTree = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();

  const [openItems, setOpenItems] = React.useState<string[]>([`visit-${props.visit.visitId}`]);

  const handleOpenChange = (event: unknown, data: TreeOpenChangeData) => {
    if (data.openItems instanceof Set) {
      setOpenItems(Array.from(data.openItems).map(String));
    } else if (Array.isArray(data.openItems)) {
      setOpenItems((data.openItems as Array<string | number>).map(String));
    } else {
      setOpenItems([]);
    }
  };

  useEffect(() => {
    props.getAllContractConceptRelationships(props.ContractId);
  }, [props.ContractId, props.visit.visitId]);

  const extraFormTypeLabel = (type: ExtraFormType): string => {
    switch (type) {
      case ExtraFormType.Additional:
        return t('visitConcepts.extraForms.additional', 'Adicionales');
      case ExtraFormType.ExtraProtocolVisit:
        return t('visitConcepts.extraForms.extraProtocolVisit', 'Visitas extra del protocolo');
      case ExtraFormType.EarlyCompletion:
        return t('visitConcepts.extraForms.earlyCompletion', 'Finalización anticipada');
      default:
        return String(type);
    }
  };

  // Resolve group label; "visit" means forms belonging to the visit (no extraFormType)
  const groupLabel = (groupKey: string): string => {
    if (groupKey === 'visit') {
      return t('visitConcepts.extraForms.visit', 'Formularios de visita');
    }
    const numeric = Number(groupKey) as ExtraFormType;
    return extraFormTypeLabel(numeric);
  };

  const groupedForms = React.useMemo(() => {
    const groups: Record<string, typeof props.visit.forms> = {};
    (props.visit.forms || []).forEach(f => {
      // Fallback: si el formulario no trae extraFormType, buscamos en sus conceptos.
      // (En algunos payloads se observa que el nivel form viene null/omitido.)
      const formLevel: any = (f as any).extraFormType; // podría venir undefined aunque el tipo lo marque requerido.
      const conceptLevel = f.concepts?.find(c => 
        c &&
        typeof c === 'object' &&
        Object.prototype.hasOwnProperty.call(c, 'extraFormType') &&
        c.extraFormType !== null &&
        c.extraFormType !== undefined
      )?.extraFormType;
      const effective = (formLevel === null || formLevel === undefined) ? conceptLevel : formLevel;
      const key = (effective === null || effective === undefined) ? 'visit' : String(effective);
      if (!groups[key]) groups[key] = [] as any;
      groups[key].push(f);
    });
    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'visit') return -1;
      if (b === 'visit') return 1;
      return Number(a) - Number(b);
    });
    return orderedKeys.map(k => [k, groups[k]] as [string, typeof props.visit.forms]);
  }, [props.visit.forms]);

  return (
    <Tree aria-label="Visit Concepts Tree" openItems={openItems} onOpenChange={handleOpenChange}>
      <VisitConceptTreeBranch
        ContractId={props.ContractId}
        label={<><span>{t('visitConcepts.labels.visit')}</span>: {props.visit.visitTitle}</>}
        value={`visit-${props.visit.visitId}`}
        icon={<CalendarLtr20Regular />}
        leaves={[
          { label: t('visitConcepts.labels.patient'), value: props.visit.numberInProtocol, isPayable: false, isProcedure: false}
        ]}
        extraFormType={null}
      >
        {groupedForms.map(([groupKey, forms]) => (
          <VisitConceptTreeBranch
            ContractId={props.ContractId}
            key={`group-${props.visit.visitId}-${groupKey}`}
            label={<><span>{t('visitConcepts.labels.formGroup', 'Grupo')}</span>: {groupLabel(groupKey)}</>}
            value={`group-${props.visit.visitId}-${groupKey}`}
            icon={<Folder20Regular />}
            extraFormType={groupKey === 'visit' ? null : Number(groupKey)}
          >
            {forms.map((form) => {
              const concepts = (form.concepts || []).filter((c): c is NonNullable<typeof c> => c !== null && c !== undefined);
              const hasRealizedConcept = concepts.some(c => Boolean(c?.realized));
              return (
                <VisitConceptTreeBranch
                  ContractId={props.ContractId}
                  key={form.patientFormId}
                  label={<><span>{t('visitConcepts.labels.form')}</span>: {form.formTitle}</>}
                  value={`form-${form.patientFormId}`}
                  formType={form.formType}
                  icon={hasRealizedConcept ? <ClipboardCheckmark20Regular /> : <ClipboardTaskListRtl20Regular />}
                  hasRealized={hasRealizedConcept}
                  extraFormType={form.extraFormType}
                >
                  {concepts.map((concept: HCConcept, idx: number) => (
                    <VisitConceptTreeLeaf
                      ContractId={props.ContractId}
                      key={concept.conceptId ?? `concept-${idx}`}
                      formName={form.formTitle}
                      itemNumber={idx}
                      label={concept.description}
                      value={undefined}
                      isPayable={concept?.isPayable}
                      isProcedure={concept?.isProcedure}
                      realized={concept.realized}
                      type={concept.type}
                    />
                  ))}
                </VisitConceptTreeBranch>
              );
            })}
          </VisitConceptTreeBranch>
        ))}
      </VisitConceptTreeBranch>
    </Tree>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(VisitConceptTree as any);
